import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import { NpsFormComponent } from '@monorepo/ui';
import {
  TREQUEST_NPS_GET_FORM,
  TRESPONSE_NPS_GET_FORM,
  TREQUEST_NPS_SUBMIT_FORM,
  TRESPONSE_NPS_SUBMIT_FORM,
  TNPS_MODAL_DISMISS_DATA,
} from '@monorepo/types';
import { NpsDataService } from '@monorepo/services';

@Injectable({
  providedIn: 'root',
})
export class NpsCommonService {
  private isNpsActive = false;

  constructor(private npsDataService: NpsDataService, private modalCtr: ModalController) {}

  /**
   * Initializes the NPS feedback form with user details and application-specific information.
   *
   * Should be called once after a successful login with the required user/app data.
   */
  initForm(data: TREQUEST_NPS_GET_FORM) {
    if (this.isNpsActive) {
      return;
    }
    this.isNpsActive = true;

    this.getNpsForm(data)
      .then((response) => {
        if (response.display_feedback_form) {
          this.validateNpsFormResponse(response);
        } else {
          this.resetFlow();
        }
      })
      .catch(() => {
        this.resetFlow();
      });
  }

  validateNpsFormResponse(response: TRESPONSE_NPS_GET_FORM) {
    const firstEmojiQuestion = response.feedback_questions.find((q) => q.answer_type === 'Emoji' && q.order_no === 1);
    if (firstEmojiQuestion) {
      this.showNpsForm(firstEmojiQuestion, response);
    } else {
      this.resetFlow();
    }
  }

  async showNpsForm(
    firstEmojiQuestion: TRESPONSE_NPS_GET_FORM['feedback_questions'][number],
    response: TRESPONSE_NPS_GET_FORM,
  ) {
    const currentModal = await this.modalCtr.create({
      component: NpsFormComponent,
      backdropDismiss: true,
      keyboardClose: true,
      componentProps: {
        feedbackQuestion: firstEmojiQuestion,
        timerValue: response.timer_value,
        timerForEnablingSubmit: response.timer_for_enabling_submit * 1000,
      },
      cssClass: 'nps-form-modal',
    });

    await currentModal.present();

    const { data } = await currentModal.onDidDismiss<TNPS_MODAL_DISMISS_DATA>();

    this.handleSubmitNPSForm({
      user_name: response.user_name,
      feedback_activation_uuid: response.feedback_form_id,
      feedback_form_data: data?.data ?? [],
      user_action: data?.action ?? 'Skip',
    });
  }

  handleSubmitNPSForm(userResponse: TREQUEST_NPS_SUBMIT_FORM) {
    this.submitNpsFeedback(userResponse).finally(() => this.resetFlow());
  }

  async getNpsForm(request: TREQUEST_NPS_GET_FORM): Promise<TRESPONSE_NPS_GET_FORM> {
    return lastValueFrom(this.npsDataService.getNpsForm(request));
  }

  async submitNpsFeedback(request: TREQUEST_NPS_SUBMIT_FORM): Promise<TRESPONSE_NPS_SUBMIT_FORM> {
    return lastValueFrom(this.npsDataService.submitNpsFeedback(request));
  }

  private resetFlow() {
    this.isNpsActive = false;
  }
}
